"use client";

import React, { useEffect, useState } from "react";
import BaseTailoringQuestions, { TailoringQuestion } from "../../shared/BaseTailoringQuestions";
import { getFY_YEAR } from "@/utils/sharepoint-env";

interface TrdRcvTailoringQuestionsProps {
  onBack?: () => void;
}

// Pre-populated table rows
const INITIAL_TABLE_ROWS = [
  {
    question: "Does the entity have a policy for expected credit loss?",
    response: "",
    remarks: "",
  },
  {
    question: "Is there any significant degree of estimation uncertainty involved in any of the assumptions or methods used to arrive at the accounting estimate?",
    response: "",
    remarks: "",
  },
  {
    question: "Is there any significant management bias involved in any of the assumptions? (The susceptibility of an accounting estimate to management bias increases with subjectivity)",
    response: "",
    remarks: "",
  },
  {
    question: "Are there any changes in management estimation making process?",
    response: "",
    remarks: "",
  },
];

// Regular questions (not in table) - TrRcv-1 and TrRcv-6
const REGULAR_QUESTIONS: TailoringQuestion[] = [
  {
    id: "TrRcv-1",
    question: "Does the entity have or is expected to have an allowance for doubtful accounts?",
    type: "radio",
    options: ["Yes", "No"],
  },
  {
    id: "TrRcv-6",
    question: "Does the entity have material foreign currency denominated receivables?",
    type: "radio",
    options: ["Yes", "No"],
  },
];

// Table question with pre-populated rows
const TABLE_QUESTION: TailoringQuestion = {
  id: "TrRcv-table",
  question: "Estimation and Policy Questions",
  type: "table",
  tableConfig: {
    columns: [
      {
        key: "question",
        label: "Question",
        type: "text",
        readOnly: true,
      },
      {
        key: "response",
        label: "Response",
        type: "dropdown",
        options: ["Yes", "No"],
      },
      {
        key: "remarks",
        label: "Remarks (if applicable)",
        type: "text",
      },
    ],
    allowAdd: false,
    allowDelete: false,
  },
};

// All questions combined
const ALL_QUESTIONS: TailoringQuestion[] = [
  ...REGULAR_QUESTIONS,
  TABLE_QUESTION,
];

export default function TrdRcvTailoringQuestions({ onBack }: TrdRcvTailoringQuestionsProps) {
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize table with pre-populated rows BEFORE BaseTailoringQuestions loads
  useEffect(() => {
    const initializeTable = async () => {
      try {
        // Check if table data exists in SharePoint file
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson(
            "Execution_TailoringQuestions_Responses.json"
          );

          let needsInitialization = false;
          let existingData: any = {};

          if (result?.success && result?.data) {
            existingData = result.data;
            const areaData = result.data["Trade Receivables"];
            const tableData = areaData?.["TrRcv-table"]?.table;
            
            // If table is empty or doesn't exist, we need to initialize it
            if (!tableData || (Array.isArray(tableData) && tableData.length === 0)) {
              needsInitialization = true;
            }
          } else {
            // No file exists, need to create it with initial data
            needsInitialization = true;
          }

          if (needsInitialization) {
            // Get company name
            let companyName = "ABC Private Limited";
            try {
              const { getCompanyName: getCompanyNameFn } = await import("@/lib/textron-data-processor");
              companyName = await getCompanyNameFn("combined");
            } catch {
              // Use default
            }

            // Ensure Trade Receivables section exists
            if (!existingData["Trade Receivables"]) {
              existingData["Trade Receivables"] = {};
            }

            // Set initial table data
            existingData["Trade Receivables"]["TrRcv-table"] = {
              table: INITIAL_TABLE_ROWS,
              lastUpdated: new Date().toISOString(),
              companyName,
            };

            // Get FY_YEAR
            const fyYear = getFY_YEAR();
            if (!fyYear) {
              throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
            }

            // Save to SharePoint
            const jsonString = JSON.stringify(existingData, null, 2);
            const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

            await (window as any).sharePointAPI.uploadFile({
              fileContent: base64Content,
              fileName: "Execution_TailoringQuestions_Responses.json",
              referenceValue: "Execution Tailoring Questions Responses",
              folderName: "juggernaut",
              fyYear: fyYear,
              sectionList: ["Execution_TailoringQuestions_Responses"],
            });

            console.log("[TrdRcv] Initialized table with pre-populated rows");
          }
          
          setIsInitialized(true);
        } else {
          setIsInitialized(true);
        }
      } catch (error) {
        console.warn("Failed to initialize table:", error);
        setIsInitialized(true); // Still render even if initialization fails
      }
    };

    // Initialize immediately, before BaseTailoringQuestions loads
    initializeTable();
  }, []);

  // Don't render BaseTailoringQuestions until initialization is complete
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-white/60">Loading...</div>
      </div>
    );
  }

  return (
    <BaseTailoringQuestions
      areaName="Trade Receivables"
      title="Trade Receivables - Tailoring Questions"
      description="Answer entity-specific questions for Trade Receivables"
      questions={ALL_QUESTIONS}
      onBack={onBack}
    />
  );
}
